/**
 * DRIVERS LIST CUSTOM COMPONENT
 *
 * This component displays a table of drivers using VersatileGrid, showing
 * driver information and their values across different scenarios.
 *
 * FEATURES:
 * - Sortable columns for all data types
 * - Search functionality across driver names
 * - Dynamic scenario columns based on available scenarios
 * - Clean, consistent styling with the report theme
 *
 * COMPONENT PROPS:
 * - plotGroup: Plot group ID for identifying the component context
 * - plotPackage: Plot package ID for identifying the specific plot
 * - index: Index of the component in the parent container
 */

import React, { useMemo } from 'react'
import VersatileGrid from '../../../ui_reusable/VersatileGrid'
import {
  GridCellSmallFadedText,
  GridCellProminantText,
  GridCellAlmostProminantText,
  GridCellHeaderProminantText,
} from '../../../ui_reusable/styles/VersatileGridUtils.styles'
import { Container } from './styles/DriversListComponent.styles'
import { useScenarioReportStore } from '../stores/scenarioReportStore'
import type { PlotGroup } from '../../../../api/generated/models/plotGroup'
import type { PlotPackage } from '../../../../api/generated/models/plotPackage'
import type { ScenarioReport } from '../../../../api/generated/models/scenarioReport'

// Type definitions for component data structures
interface Driver {
  driver_id: number
  name: string
  source: string
  units: string
  original_end_value: number
  mutated_end_value: number
  has_been_adjusted: boolean
}

interface Scenario {
  id: number
  name: string
  predicted_model_end_value: number
  drivers?: Driver[]
}

interface ScenarioData {
  original_model_end_value: number
  scenarios: Scenario[]
}

interface DriversListComponentProps {
  plotGroup?: PlotGroup
  plotPackage?: PlotPackage
  index?: number
}

interface DriverRow {
  id: number
  driver: string
  source: string
  units: string
  unadjusted: number | string
  _searchId: string // Required by VersatileGrid for search functionality
  _originalIndex: number // Required by VersatileGrid for indexing
  [key: string]: unknown // For dynamic scenario columns like scenario_1, scenario_2, etc.
}

interface GridHeader {
  title: string
  key: string
  type: 'string' | 'number' | 'boolean' | 'datetime'
  isSortable: boolean
  isSearchable: boolean
  center?: boolean
  overrideJSX?: React.ReactNode
}

const DriversListComponent: React.FC<DriversListComponentProps> = ({
  plotGroup,
  plotPackage,
  index,
}) => {
  // Get data from the store state without reactive subscription
  const { anyReport: initialReportData } =
    useScenarioReportStore.getState() as { anyReport: ScenarioReport | null }

  // Helper function to get typed scenario data
  const getScenarioData = (): ScenarioData | null => {
    return initialReportData?.scenario as ScenarioData | null
  }

  // Process the data to create driver rows
  const driversData = useMemo((): DriverRow[] => {
    const scenarioData = getScenarioData()

    if (!scenarioData?.scenarios) {
      return []
    }

    const scenarios = scenarioData.scenarios || []

    // Extract unique drivers from all scenarios
    const driverMap = new Map<
      number,
      {
        id: number
        name: string
        source: string
        units: string
        original_end_value: number
      }
    >()

    scenarios.forEach((scenario) => {
      scenario.drivers?.forEach((driver) => {
        if (driver.original_end_value !== driver.mutated_end_value) {
          if (!driverMap.has(driver.driver_id)) {
            driverMap.set(driver.driver_id, {
              id: driver.driver_id,
              name: driver.name,
              source: driver.source,
              units: driver.units,
              original_end_value: driver.original_end_value,
            })
          }
        }
      })
    })

    const uniqueDrivers = Array.from(driverMap.values())

    return uniqueDrivers.map((driver, index) => {
      const driverRow: DriverRow = {
        id: driver.id,
        driver: driver.name,
        source: driver.source,
        units: driver.units,
        unadjusted:
          driver.units === 'percentage'
            ? Math.round(driver.original_end_value) + '%'
            : Math.round(driver.original_end_value),
        _searchId: `driver_${driver.id}`,
        _originalIndex: index,
      }

      // Add scenario-specific mutated values
      scenarios.forEach((scenario: Scenario) => {
        const driverValue = scenario.drivers?.find(
          (d: Driver) => d.driver_id === driver.id,
        )
        if (driverValue) {
          const mutatedValue = driverValue.mutated_end_value
          driverRow[`scenario_${scenario.id}`] =
            driverValue.units === 'percentage'
              ? Math.round(mutatedValue) + '%'
              : Math.round(mutatedValue)
        } else {
          driverRow[`scenario_${scenario.id}`] = null
        }
      })

      return driverRow
    })
    /* eslint-disable-next-line */
  }, [initialReportData])

  // Create dynamic headers based on scenarios
  const headers = useMemo((): GridHeader[] => {
    const baseHeaders: GridHeader[] = [
      {
        title: 'Drivers',
        key: 'driver',
        type: 'string',
        isSortable: true,
        isSearchable: true,
        overrideJSX: <GridCellSmallFadedText>Drivers</GridCellSmallFadedText>,
      },
      {
        title: 'Source',
        key: 'source',
        type: 'string',
        isSortable: true,
        isSearchable: true,
        center: true,
        overrideJSX: <GridCellSmallFadedText>Source</GridCellSmallFadedText>,
      },
      {
        title: 'Units',
        key: 'units',
        type: 'string',
        isSortable: true,
        isSearchable: true,
        center: true,
        overrideJSX: <GridCellSmallFadedText>Units</GridCellSmallFadedText>,
      },
      {
        title: 'Unadjusted',
        key: 'unadjusted',
        type: 'number',
        isSortable: true,
        isSearchable: false,
        center: true,
        overrideJSX: (
          <GridCellHeaderProminantText>Unadjusted</GridCellHeaderProminantText>
        ),
      },
    ]

    // Add scenario columns
    const scenarioData = getScenarioData()
    const scenarios = scenarioData?.scenarios || []
    const scenarioHeaders: GridHeader[] = scenarios.map(
      (scenario: Scenario) => ({
        title: scenario.name,
        key: `scenario_${scenario.id}`,
        type: 'number' as const,
        isSortable: true,
        isSearchable: false,
        center: true,
        overrideJSX: (
          <GridCellHeaderProminantText>
            {scenario.name}
          </GridCellHeaderProminantText>
        ),
      }),
    )

    return [...baseHeaders, ...scenarioHeaders]
    /* eslint-disable-next-line */
  }, [initialReportData])

  // Create dynamic grid template columns
  const gridTemplateColumns = useMemo(() => {
    const scenarioData = getScenarioData()
    const scenarios = scenarioData?.scenarios || []
    const baseColumns = '2fr 1fr 1fr 1fr' // Driver, Source, Units, Unadjusted
    const scenarioColumns = scenarios.map(() => '1fr').join(' ')
    return `${baseColumns} ${scenarioColumns}`.trim()
    /* eslint-disable-next-line */
  }, [initialReportData])

  // Render row function
  const renderRow = (
    rowData: unknown,
    rowIdx: number,
    originalIndex: number,
  ): React.ReactNode[] => {
    const driverRowData = rowData as DriverRow
    const scenarioData = getScenarioData()
    const scenarios = scenarioData?.scenarios || []

    const baseColumns: React.ReactNode[] = [
      <GridCellAlmostProminantText>
        {driverRowData.driver}
      </GridCellAlmostProminantText>,
      <GridCellSmallFadedText>
        {driverRowData.source || 'Unknown'}
      </GridCellSmallFadedText>,
      <GridCellSmallFadedText>{driverRowData.units}</GridCellSmallFadedText>,
      <GridCellProminantText>
        {driverRowData.unadjusted !== null ? driverRowData.unadjusted : 'N/A'}
      </GridCellProminantText>,
    ]

    // Add scenario columns
    const scenarioColumns: React.ReactNode[] = scenarios.map(
      (scenario: Scenario) => {
        const value = driverRowData[`scenario_${scenario.id}`]
        const displayValue =
          value !== null && value !== undefined ? String(value) : 'N/A'
        return (
          <GridCellAlmostProminantText key={scenario.id}>
            {displayValue}
          </GridCellAlmostProminantText>
        )
      },
    )

    return [...baseColumns, ...scenarioColumns]
  }

  return (
    <Container>
      <VersatileGrid
        gridContainerStyle={{ gridTemplateColumns }}
        headers={headers}
        data={driversData}
        renderRow={renderRow}
        loadingMessage="Loading drivers..."
        isSearchable={false}
        paddingInGridHeader="0.0rem 0rem 0rem 0rem"
      />
    </Container>
  )
}

export default DriversListComponent
